import { type FC, Fragment, type MouseEvent, useMemo } from "react";

import { Skeleton } from "@snapchat/snap-design-system";

import { DestinationTypeBm, type DistributionRestriction, type LensDestination } from "../../../gql-generated/types";
import { makeDistributionRestrictionsResult } from "../../hooks/makeDistributionRestrictionsResult";

import { LensDestinationRow } from "./LensDestinationRow";
import { LensDestinationsTooltipContainer } from "./LensDestinationsTooltip.styles";
import { LensDestinationsDivider } from "./LensDestinationsTooltipContent.styles";
import { LensDestinationsTooltipHeader } from "./LensDestinationsTooltipHeader";

interface LensDestinationsTooltipContentProps {
    loading: boolean;
    destinations: readonly LensDestination[];
    numDestinations?: number;
    userId: string;
    onLensDestinationHeaderClick?: (event: MouseEvent<HTMLElement>) => void;
    distributionRestrictionsList: readonly DistributionRestriction[];
}

const destinationSortOrder: Record<DestinationTypeBm, number> = {
    [DestinationTypeBm.DestinationTypeUnset]: 0,
    [DestinationTypeBm.DestinationTypeAdAccount]: 1,
    [DestinationTypeBm.DestinationTypePublicProfile]: 2,
    [DestinationTypeBm.DestinationTypeUser]: 3,
};
export const LensDestinationsTooltipContent: FC<LensDestinationsTooltipContentProps> = ({
    loading,
    destinations,
    numDestinations,
    userId,
    distributionRestrictionsList,
    onLensDestinationHeaderClick,
}) => {
    const destinationsSorted = useMemo(
        () =>
            [...destinations].sort(
                (a, b) => destinationSortOrder[b.destinationType] - destinationSortOrder[a.destinationType],
            ),
        [destinations],
    );

    const { hasLensLevelDistributionRestrictions, hasProfileDistributionRestrictions } = useMemo(() => {
        return makeDistributionRestrictionsResult(distributionRestrictionsList);
    }, [distributionRestrictionsList]);

    if (loading) {
        return (
            <LensDestinationsTooltipContainer className="destination-tooltip" data-testid="destination-tooltip">
                <Skeleton
                    data-testid="destination-tooltip-skeleton"
                    height={numDestinations ? numDestinations * 20 : 50}
                />
            </LensDestinationsTooltipContainer>
        );
    }

    return (
        <LensDestinationsTooltipContainer className="destination-tooltip" data-testid="destination-tooltip">
            {distributionRestrictionsList.length > 0 && (
                <LensDestinationsTooltipHeader
                    onTriggerClick={(event: MouseEvent<HTMLElement>) => {
                        onLensDestinationHeaderClick?.(event);
                    }}
                />
            )}
            {destinationsSorted.map((dest) => (
                <Fragment key={dest.id}>
                    <LensDestinationRow
                        destination={dest}
                        userId={userId}
                        hasRestrictions={
                            dest.destinationType === DestinationTypeBm.DestinationTypePublicProfile ||
                            dest.destinationType === DestinationTypeBm.DestinationTypeUser
                                ? hasProfileDistributionRestrictions || hasLensLevelDistributionRestrictions
                                : hasLensLevelDistributionRestrictions
                        }
                    />
                    {dest.destinationType === DestinationTypeBm.DestinationTypePublicProfile && (
                        <LensDestinationsDivider />
                    )}
                </Fragment>
            ))}
        </LensDestinationsTooltipContainer>
    );
};
